/**
 * OrganizationPayerDocsSection
 *
 * Admin view of payer docs for an organization.
 * Uses the admin endpoint to list all payer docs.
 */

import { useMemo } from 'react';
import { Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { enhancedApi } from '@store/generated/payer-docs';
import type { PayerDocResponse } from '@store/generated/payer-docs';

interface OrganizationPayerDocsSectionProps {
  organizationId: string;
}

// Use the admin endpoint directly (not the provider one)
const { useGetV2AdminOrgsByOrganizationIdBillingPayerDocsQuery } = enhancedApi;

function formatDate(d: string | null | undefined): string {
  if (!d) return '\u2014';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
}

function getPayerName(doc: PayerDocResponse): string {
  // Try payer_name first, then dig into payload
  if (doc.payer_name) return doc.payer_name;
  const payload = doc.payload as any;
  if (payload?.payer?.name) return payload.payer.name;
  return doc.payer_key;
}

function getPayerType(doc: PayerDocResponse): string {
  const payload = doc.payload as any;
  return payload?.payer?.payer_type?.replace(/_/g, ' ') || '\u2014';
}

function getPayerState(doc: PayerDocResponse): string {
  const payload = doc.payload as any;
  return payload?.payer?.state || '\u2014';
}

function getRateCount(doc: PayerDocResponse): number {
  const payload = doc.payload as any;
  return payload?.reimbursement?.rates?.length || 0;
}

function getFlagCount(doc: PayerDocResponse): number {
  const payload = doc.payload as any;
  return (payload?.audit_flags || []).filter((f: any) => f.severity === 'critical' || f.severity === 'high').length;
}

function getConfidence(doc: PayerDocResponse): string | null {
  const payload = doc.payload as any;
  return payload?._meta?.extraction_confidence || null;
}

export function OrganizationPayerDocsSection({ organizationId }: OrganizationPayerDocsSectionProps) {
  const { data, isLoading, isError } = useGetV2AdminOrgsByOrganizationIdBillingPayerDocsQuery(
    { organizationId },
    { skip: !organizationId }
  );

  const docs = useMemo(() => data?.items || [], [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error" sx={{ py: 2 }}>
        Failed to load payer docs for this organization.
      </Typography>
    );
  }

  if (docs.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No payer docs on file for this organization.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {docs.length} payer contract{docs.length !== 1 ? 's' : ''} extracted
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Payer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Rates</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Flags</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map((doc) => {
              const confidence = getConfidence(doc);
              const flagCount = getFlagCount(doc);

              return (
                <TableRow key={doc.payer_doc_id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{getPayerName(doc)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {doc.payer_key}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{getPayerType(doc)}</TableCell>
                  <TableCell>{getPayerState(doc)}</TableCell>
                  <TableCell align="right">{getRateCount(doc)}</TableCell>
                  <TableCell align="right">
                    {flagCount > 0 ? (
                      <Chip label={flagCount} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                    ) : (
                      '\u2014'
                    )}
                  </TableCell>
                  <TableCell>
                    {confidence && (
                      <Chip
                        label={confidence}
                        size="small"
                        color={confidence === 'high' ? 'success' : confidence === 'medium' ? 'warning' : 'error'}
                        sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(doc.updated_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
